import { rackAabb, type PlanRect, type RackPlacement } from './plan-geometry.js';

/**
 * REJANING KO'RINISH OYNASI — masshtab va surilish.
 *
 * Nega alohida fayl: bu sof hisob va uni komponent ichida qoldirish uni
 * sinovdan tashqarida qoldirardi. Ko'rinish matematikasi esa jimgina
 * buziladigan joy — chegaralar bir piksel siljisa, operator rejani
 * yo'qotib qo'yadi va qaytish yo'lini topa olmaydi.
 *
 * Koordinatalar `viewBox` birligida, ya'ni CHEKKA (`PAD`) qo'shilgan
 * maydonda. Pol koordinatasidan farqi shu — funksiyalar `pad` ni argument
 * sifatida oladi va o'zi qo'shadi.
 */

export interface PlanView {
  x: number;
  y: number;
  /** 1 — butun pol ekranda; 4 — to'rt marta yaqin. */
  zoom: number;
}

/** Chizmaning boshlang'ich (butun pol ko'rinadigan) o'lchami. */
export interface PlanBase {
  w: number;
  h: number;
}

export const MIN_ZOOM = 1;
export const MAX_ZOOM = 40;

/** Zonaga yaqinlashganda chetlarda qoldiriladigan bo'sh joy. */
const FOCUS_MARGIN_RATIO = 0.35;
const FOCUS_MARGIN_MIN_M = 1;

const clampNumber = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

/**
 * Ko'rinishni chegaraga soladi.
 *
 * Chetda ATAYLAB yarim ekran bo'sh joy qoldiriladi: operator ombor
 * chegarasidagi javonni markazga qo'yib qarashi kerak bo'ladi. Undan
 * nariga surilmaydi — aks holda ekranda bo'sh fon qolib, "hammasi qayoqqa
 * ketdi?" degan holat paydo bo'lardi.
 */
export const clampView = (view: PlanView, base: PlanBase): PlanView => {
  const zoom = clampNumber(view.zoom, MIN_ZOOM, MAX_ZOOM);
  const w = base.w / zoom;
  const h = base.h / zoom;
  return {
    zoom,
    x: clampNumber(view.x, -w / 2, base.w - w / 2),
    y: clampNumber(view.y, -h / 2, base.h - h / 2),
  };
};

/**
 * To'rtburchakni (pol koordinatasida) kadrga oladigan ko'rinish.
 *
 * Masshtab IKKALA o'q bo'yicha sig'ishi shart, shuning uchun kichigi
 * olinadi: faqat kenglik bo'yicha hisoblansa, cho'zinchoq zona balandligi
 * bo'yicha kesilib qolardi.
 */
export const fitView = (rect: PlanRect, base: PlanBase, pad: number): PlanView => {
  const margin = Math.max(rect.w, rect.d) * FOCUS_MARGIN_RATIO + FOCUS_MARGIN_MIN_M;
  const w = rect.w + margin * 2;
  const h = rect.d + margin * 2;
  const zoom = clampNumber(Math.min(base.w / w, base.h / h), MIN_ZOOM, MAX_ZOOM);

  const centerX = rect.x + rect.w / 2 + pad;
  const centerY = rect.y + rect.d / 2 + pad;
  return clampView(
    { zoom, x: centerX - base.w / zoom / 2, y: centerY - base.h / zoom / 2 },
    base,
  );
};

/**
 * Kursor ostidagi nuqtani JOYIDA qoldirib masshtabni o'zgartiradi.
 *
 * Markazga nisbatan yaqinlashish qaralayotgan javonni kadrdan chiqarib
 * yuborardi: odam g'ildirakni aylantirib, keyin uni qidirishga tushardi.
 */
export const zoomAt = (
  view: PlanView,
  base: PlanBase,
  /** Kursor nuqtasi — `viewBox` birligida. */
  anchor: { x: number; y: number },
  factor: number,
): PlanView => {
  const zoom = clampNumber(view.zoom * factor, MIN_ZOOM, MAX_ZOOM);
  const ratio = view.zoom / zoom;
  return clampView(
    {
      zoom,
      x: anchor.x - (anchor.x - view.x) * ratio,
      y: anchor.y - (anchor.y - view.y) * ratio,
    },
    base,
  );
};

/**
 * Zona va javonlarning umumiy gabariti — "mazmunga sig'dirish" uchun.
 *
 * `null` — chizmada hech nima yo'q. Bunday holatda sig'dirish ma'nosiz va
 * tugma o'chirilgan bo'ladi.
 */
export const contentBounds = (
  zones: readonly { plan: PlanRect }[],
  racks: readonly RackPlacement[],
): PlanRect | null => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const zone of zones) {
    minX = Math.min(minX, zone.plan.x);
    minY = Math.min(minY, zone.plan.y);
    maxX = Math.max(maxX, zone.plan.x + zone.plan.w);
    maxY = Math.max(maxY, zone.plan.y + zone.plan.d);
  }
  for (const rack of racks) {
    const box = rackAabb(rack);
    minX = Math.min(minX, box.x);
    minY = Math.min(minY, box.y);
    maxX = Math.max(maxX, box.x + box.w);
    maxY = Math.max(maxY, box.y + box.d);
  }

  if (minX === Infinity) return null;
  return { x: minX, y: minY, w: maxX - minX, d: maxY - minY };
};
